import React, { createContext, useState, useEffect, useContext } from 'react';
import { ConfigProvider, theme, App } from 'antd';

const ThemeContext = createContext();

// An embed block (/embed/..., components/Embed/) is shown inside other
// sites, so it takes the theme the embedding page asked for (?theme=light|dark),
// else the viewer's system preference, and never touches the theme the
// viewer saved for the app itself.
const isEmbedPage = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/');

function initialDarkMode() {
  if (isEmbedPage()) {
    const wanted = new URLSearchParams(window.location.search).get('theme');
    if (wanted === 'dark' || wanted === 'light') return wanted === 'dark';
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  }
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === null) return true; // default to dark mode
  return savedTheme === 'dark';
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(initialDarkMode);

  useEffect(() => {
    if (!isEmbedPage()) localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ConfigProvider
        theme={{
          algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        }}
      >
        <App>{children}</App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};